import { isNonEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { FormattedMessage } from 'components/shared/Intl';
import type { IntlKey } from 'locales';
import type { SimpleGridFieldData, SimpleGridFieldMetadata } from 'models/forms/fields';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';
import type { RowData } from './GridColumns';
import { GridColumns, getDefaultValueForNewRow } from './GridColumns';
import { GridRow } from './GridRow';
import { Warning } from './Warning';

export const SimpleGridField: FC<ProductNodeBuilderProps> = ({ node: { id, labelSize }, workspaceType, tabIndex }) => {
  const [data, setData] = useData<SimpleGridFieldData>(id);
  const [isInsertionMode, setInsertionMode] = useState<boolean>(false);
  const hasError = useIsFieldInvalid(id);
  const metadata = useFieldMetadata<SimpleGridFieldMetadata>(id)!;
  const columnMetadata = metadata.columnFieldDefinitions!;

  const onAddRowClicked = useCallback(() => setInsertionMode(true), []);

  const rows = useMemo<ReadonlyArray<RowData>>(() => {
    if (isInsertionMode) {
      // empty row with null values
      const newEmptyRow = columnMetadata.reduce<RowData>((acc, colMeta) => {
        acc[colMeta.id] = getDefaultValueForNewRow(colMeta);
        return acc;
      }, {});
      return [...(data || []), newEmptyRow];
    } else {
      return data || [];
    }
  }, [isInsertionMode, columnMetadata, data]);

  const onRowUpdated = useCallback(
    (rowIndex: number, value: RowData) => setData([...rows.slice(0, rowIndex), value, ...rows.slice(rowIndex + 1)]),
    [rows, setData],
  );

  const onRowRemoved = useCallback(
    (rowIndex: number) => {
      const updatedData = [...rows.slice(0, rowIndex), ...rows.slice(rowIndex + 1)];
      setData(updatedData);
    },
    [rows, setData],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Rerun this effect when data changes
  useEffect(() => setInsertionMode(false), [data]);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${metadata.translationKey}` as any}
      labelSize={labelSize}
      workspaceType={workspaceType}
    >
      <div className={classNames({ 'border border-warning': hasError })}>
        {rows.map((row, index) => (
          <GridRow key={index} fieldId={id} isLoading={false} index={index} removeRow={onRowRemoved}>
            <GridColumns fieldId={id} rowIndex={index} row={row} setRowValue={onRowUpdated} metadata={columnMetadata} />
          </GridRow>
        ))}
        <div className="d-flex ms-0">
          <button
            type="button"
            id={id + '-add-line'}
            className="btn btn-flat-primary btn-icon-start text-start"
            disabled={isInsertionMode}
            onClick={onAddRowClicked}
            data-e2e={id + '-item-add'}
            tabIndex={tabIndex}
          >
            <i className="icon">add</i>
            <FormattedMessage id="form.Add" />
          </button>
          {isNonEmpty(data) && <Warning warningId={metadata.infoTranslationKey as IntlKey} />}
        </div>
      </div>
    </BlockOrientationFormGroup>
  );
};

SimpleGridField.displayName = 'SimpleGridField';
